// plan-03 S01: terminal layout + status bar mount — bible §14
// plan-12 S05: status-bar vitals cluster (heartbeat, drift, quorum)

import { utcNow, bpmPhase } from '../core/time'
import { mountMuteAffordance } from '../audio/mute'
import { load } from '../core/save'
import { utcMinute, fromString } from '../core/seed'

export interface StatusBarController {
  updateClock: () => void
  toggleNodes: () => void
  onNodesToggle: ((open: boolean) => void) | null
}

// plan-12 S05: drift state for vitals
let driftValue = 0
let driftTarget = 0

export function glitchDrift(): void {
  driftValue = (Math.random() - 0.5) * 0.96 // [-0.48, +0.48]
  setTimeout(() => { driftValue = 0 }, 240)
}

export function mountStatusBar(parent: HTMLElement, opts: { quiet: boolean }): StatusBarController {
  const ctrl: StatusBarController = {
    updateClock() {},
    toggleNodes() {},
    onNodesToggle: null,
  }

  // left: brand
  const left = document.createElement('div')
  left.className = 'vt-sb-left'

  const brand = document.createElement('span')
  brand.className = 'vt-sb-brand-detail'
  brand.textContent = '◇ pa.oracle'
  left.appendChild(brand)

  const signal = document.createElement('span')
  signal.className = 'vt-sb-brand-detail'
  signal.textContent = ' ▮▮▮▮▮ 48hz nominal'
  left.appendChild(signal)

  // plan-12 S05: vitals cluster (wide only — hidden by CSS on narrow)
  const vitals = document.createElement('span')
  vitals.className = 'vt-sb-vitals'
  vitals.style.cssText = 'margin-left: 6px; display: inline-flex; align-items: center; gap: 6px;'

  // heartbeat indicator: hb ▮
  const hbLabel = document.createElement('span')
  hbLabel.textContent = 'hb'
  hbLabel.style.color = 'var(--vt-green-dim)'
  vitals.appendChild(hbLabel)

  const hbGlyph = document.createElement('span')
  hbGlyph.className = 'vt-sb-heartbeat'
  hbGlyph.textContent = '▮'
  hbGlyph.style.color = 'var(--vt-king-green)'
  vitals.appendChild(hbGlyph)

  // drift reading: drift +0.000
  const driftLabel = document.createElement('span')
  driftLabel.textContent = 'drift'
  driftLabel.style.color = 'var(--vt-green-dim)'
  vitals.appendChild(driftLabel)

  const driftVal = document.createElement('span')
  driftVal.className = 'vt-sb-drift'
  driftVal.textContent = '+0.000'
  driftVal.style.color = 'var(--vt-green-dim)'
  driftVal.style.fontVariantNumeric = 'tabular-nums'
  vitals.appendChild(driftVal)

  // quorum: q 48/48
  const qLabel = document.createElement('span')
  qLabel.textContent = 'q'
  qLabel.style.color = 'var(--vt-green-dim)'
  vitals.appendChild(qLabel)

  const qVal = document.createElement('span')
  qVal.textContent = '48/48'
  qVal.style.color = 'var(--vt-king-green)'
  vitals.appendChild(qVal)

  left.appendChild(vitals)

  // narrow-only drift (standalone, shown via CSS on narrow/compact)
  const narrowDriftLabel = document.createElement('span')
  narrowDriftLabel.className = 'vt-sb-narrow-drift'
  narrowDriftLabel.textContent = 'drift'
  narrowDriftLabel.style.color = 'var(--vt-green-dim)'
  const narrowDriftVal = document.createElement('span')
  narrowDriftVal.className = 'vt-sb-narrow-drift-val'
  narrowDriftVal.textContent = '+0.000'
  narrowDriftVal.style.color = 'var(--vt-green-dim)'
  narrowDriftVal.style.fontVariantNumeric = 'tabular-nums'
  left.appendChild(narrowDriftLabel)
  left.appendChild(narrowDriftVal)

  // center: UTC clock
  const center = document.createElement('div')
  center.className = 'vt-sb-center'
  const clock = document.createElement('span')
  center.appendChild(clock)

  ctrl.updateClock = () => {
    const { hh, mm, ss } = utcNow()
    clock.textContent = `${hh}:${mm}:${ss} UTC`
    // pulse at :48 mark
    if (ss === '48') {
      clock.classList.remove('vt-sb-pulse')
      void clock.offsetWidth
      clock.classList.add('vt-sb-pulse')
    }
  }
  ctrl.updateClock()

  // right: mute + directory toggles
  const right = document.createElement('div')
  right.className = 'vt-sb-right'

  // mount mute toggle first (direct child, no wrapper — ensures alignment with dir button)
  if (!opts.quiet) {
    mountMuteAffordance(right)
  }

  const nodesBtn = document.createElement('span')
  nodesBtn.id = 'vt-nodes-toggle'
  nodesBtn.textContent = '[ + dir ]'
  nodesBtn.setAttribute('role', 'button')
  nodesBtn.setAttribute('tabindex', '0')
  nodesBtn.setAttribute('aria-label', 'toggle directory panel')
  nodesBtn.style.cursor = 'pointer'
  let nodesOpen = false
  nodesBtn.addEventListener('click', () => {
    // plan-10 S04: [ + dir ] mirrors directory command via stage.toggle
    import('../widgets/stage/stage').then(({ getStage }) => {
      const stage = getStage()
      if (stage) {
        stage.toggle('directory')
      }
    }).catch(() => {
      // fallback: toggle locally
      nodesOpen = !nodesOpen
      nodesBtn.textContent = nodesOpen ? '[ - dir ]' : '[ + dir ]'
      ctrl.onNodesToggle?.(nodesOpen)
    })
  })
  nodesBtn.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      nodesBtn.click()
    }
  })
  right.appendChild(nodesBtn)

  ctrl.toggleNodes = () => {
    nodesOpen = !nodesOpen
    nodesBtn.textContent = nodesOpen ? '[ - dir ]' : '[ + dir ]'
    ctrl.onNodesToggle?.(nodesOpen)
  }

  // plan-14 S03: reserved chip-row sits between center and right; chips mount here
  const chipRow = document.createElement('div')
  chipRow.id = 'vt-sb-chip-row'
  chipRow.className = 'vt-sb-chip-row'

  parent.appendChild(left)
  parent.appendChild(center)
  parent.appendChild(chipRow)

  // plan-17 S03: signal-strength chip in chip-row (after chipRow is declared)
  mountSignalChip(chipRow)

  parent.appendChild(right)

  // clock ticks every second
  const clockInterval = setInterval(ctrl.updateClock, 1000)

  // plan-12 S05: vitals tick — heartbeat blinks at 48 BPM in phase with cursor
  // drift wobbles via Brownian motion bounded [-0.048, +0.048]
  const vitalsInterval = setInterval(() => {
    // heartbeat: sync with bpmPhase (same source as cursor)
    hbGlyph.style.opacity = bpmPhase() ? '1' : '0.15'

    // drift: Brownian wobble
    driftTarget += (Math.random() - 0.5) * 0.016
    driftTarget = Math.max(-0.048, Math.min(0.048, driftTarget))
    driftValue += (driftTarget - driftValue) * 0.1
    const sign = driftValue >= 0 ? '+' : ''
    driftVal.textContent = sign + driftValue.toFixed(3)
    narrowDriftVal.textContent = sign + driftValue.toFixed(3)
  }, 1600) // update every 1.6s per plan spec

  // cleanup not needed — status bar lives for page lifetime
  void clockInterval
  void vitalsInterval

  return ctrl
}

export function mountTerminalViewport(
  parent: HTMLElement,
): { history: HTMLDivElement; inputEl: HTMLInputElement; form: HTMLFormElement } {
  const wrapper = document.createElement('div')
  wrapper.setAttribute('role', 'region')
  wrapper.setAttribute('aria-label', 'oracle terminal')
  wrapper.className = 'vt-terminal-wrapper'
  wrapper.style.cssText = `
    width: 100%;
    height: 100%;
    min-height: 0;
    border: 1px solid var(--vt-green-dim);
    box-shadow: 0 0 24px rgba(0,180,0,0.06);
    background: var(--vt-screen-bg);
    display: flex;
    flex-direction: column;
    position: relative;
    z-index: var(--vt-z-terminal, 6);
  `

  // history area
  const history = document.createElement('div')
  history.className = 'vt-history'
  history.setAttribute('aria-live', 'polite')
  history.style.cssText = `
    flex: 1 1 auto;
    overflow-y: auto;
    overflow-anchor: auto;
    padding: 12px 16px;
    font-family: var(--vt-font-mono);
    font-size: var(--vt-fs-body);
    line-height: var(--vt-line-height);
    color: var(--vt-term-green);
    word-wrap: break-word;
    overflow-wrap: break-word;
    scrollbar-width: thin;
    scrollbar-color: var(--vt-green-dim) transparent;
  `

  // input form
  const form = document.createElement('form')
  form.style.cssText = `
    display: flex;
    align-items: center;
    padding: 6px 16px 8px;
    border-top: 1px solid var(--vt-outline);
    font-family: var(--vt-font-mono);
    font-size: var(--vt-fs-body);
  `
  // zsh-style prompt: oracle@consensus ~ %
  // colored segments like oh-my-zsh intheloop theme
  const promptUser = document.createElement('span')
  promptUser.textContent = 'oracle'
  promptUser.style.color = 'var(--vt-king-green)'
  promptUser.style.fontWeight = '500'
  form.appendChild(promptUser)

  const promptAt = document.createElement('span')
  promptAt.textContent = '@'
  promptAt.style.color = 'var(--vt-green-dim)'
  form.appendChild(promptAt)

  const promptHost = document.createElement('span')
  promptHost.textContent = 'consensus'
  promptHost.style.color = 'var(--vt-amber)'
  form.appendChild(promptHost)

  const promptPath = document.createElement('span')
  promptPath.textContent = ' ~ '
  promptPath.style.color = 'var(--vt-green-dim)'
  form.appendChild(promptPath)

  const promptChar = document.createElement('span')
  promptChar.textContent = '% '
  promptChar.style.color = 'var(--vt-term-green)'
  form.appendChild(promptChar)

  const inputEl = document.createElement('input')
  inputEl.type = 'text'
  inputEl.autocomplete = 'off'
  inputEl.spellcheck = false
  inputEl.setAttribute('aria-label', 'oracle command input')
  inputEl.style.cssText = `
    flex: 1;
    background: transparent;
    border: none;
    outline: none;
    color: var(--vt-king-green);
    font-family: inherit;
    font-size: inherit;
    caret-color: transparent;
  `
  form.appendChild(inputEl)

  // cursor element (rendered by cursor.ts)
  const cursorEl = document.createElement('span')
  cursorEl.className = 'vt-cursor'
  cursorEl.textContent = '▮'
  form.appendChild(cursorEl)

  wrapper.appendChild(history)
  wrapper.appendChild(form)
  parent.appendChild(wrapper)

  return { history, inputEl, form }
}

// plan-17 S03: signal-strength chip
let signalBars: HTMLElement | null = null
let signalStrength = 5

function mountSignalChip(chipRow: HTMLElement): void {
  const chip = document.createElement('span')
  chip.className = 'vt-sb-chip vt-sig-chip'
  chip.setAttribute('aria-hidden', 'true')

  const label = document.createElement('span')
  label.textContent = ' ◇ sig: '
  chip.appendChild(label)

  signalBars = document.createElement('span')
  signalBars.className = 'vt-sig-bars'
  chip.appendChild(signalBars)

  chipRow.appendChild(chip)

  // tooltip on wide
  chip.title = '> consensus signal. drift: probably.'

  // initial render
  updateSignalStrength()

  // update every 4s
  setInterval(updateSignalStrength, 4000)
}

function computeStrength(): number {
  const s = load()
  const events = s.events
  const seed = fromString(utcMinute())
  const bars = Math.floor((events / 48) + (seed % 4))
  return Math.max(1, Math.min(5, bars))
}

function updateSignalStrength(): void {
  signalStrength = computeStrength()
  renderSignalBars()
}

function renderSignalBars(): void {
  if (!signalBars) return
  const filled = '▮'.repeat(signalStrength)
  const empty = '▯'.repeat(5 - signalStrength)
  signalBars.textContent = filled + empty
}

export function dropSignalStrength(): void {
  signalStrength = 1
  renderSignalBars()
  setTimeout(() => {
    const target = computeStrength()
    const start = 1
    const step = Math.ceil((target - start) / 5)
    let current = start
    const iv = setInterval(() => {
      current = Math.min(current + step, target)
      signalStrength = current
      renderSignalBars()
      if (current >= target) clearInterval(iv)
    }, 400)
  }, 240)
}
